import React from 'react';
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    Field,
    Input,
    Select,
    Tab,
    TabList,
    Text,
    makeStyles,
    mergeClasses,
    shorthands,
    tokens,
} from '@fluentui/react-components';
import { Search24Regular } from '@fluentui/react-icons';
import { Dismiss32Regular } from '@fluentui/react-icons';
import { ChatListVm, ManageChatsTabValue, ProviderFilterValue, SortValue } from '../../hooks/useChatList';
import useManageChatsDialog from '../../hooks/useManageChatsDialog';
import ConfirmDialog from '../../../../shared/ui/ConfirmDialog';

import { AlwaysOnScrollBarStyles, customColors, customFonts } from '../../../../styles';

const useStyles = makeStyles({
    surface: {
        width: 'min(1040px, calc(100vw - 32px))',
        maxWidth: '1040px',
        minHeight: 'min(720px, calc(100vh - 48px))',
        ...shorthands.borderRadius('20px'),
        ...shorthands.padding('0px'),
        overflow: 'hidden',
    },
    body: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: 'inherit',
        backgroundColor: customColors.protivitiOffWhite,
    },
    header: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        ...shorthands.gap('16px'),
        ...shorthands.padding('28px', '28px', '20px', '28px'),
        backgroundColor: customColors.white,
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    headerText: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap('6px'),
        minWidth: 0,
    },
    title: {
        fontFamily: customFonts.TitleFont,
        fontSize: '34px',
        lineHeight: '1',
        color: customColors.summarizeButtonDarker,
        margin: '0',
    },
    closeButton: {
        minWidth: '40px',
        width: '40px',
        height: '40px',
        color: customColors.summarizeButtonDarker,
        backgroundColor: 'transparent',
        ...shorthands.border('none'),
        ...shorthands.padding('0px'),
        flexShrink: 0,
        ':hover': {
            backgroundColor: tokens.colorNeutralBackground2,
        },
    },
    subtitle: {
        fontFamily: customFonts.Lato,
        fontSize: '14px',
        color: customColors.buttonTextDarkBlue,
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        ...shorthands.padding('22px', '28px', '0px', '28px'),
        ...shorthands.gap('16px'),
    },
    tabBar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: customColors.white,
        ...shorthands.borderBottom('5px', 'solid', 'rgb(0 0 0 / 10%)'),
        ...shorthands.padding('8px', '16px'),
        ...shorthands.borderRadius('18px', '18px', '0px', '0px'),
        '@media (max-width: 640px)': {
            alignItems: 'flex-start',
            flexDirection: 'column',
            ...shorthands.gap('8px'),
        },
    },
    tabListRoot: {
        '& .fui-Tab__icon': {
            marginRight: '6px',
        },
    },
    tab: {
        ...shorthands.margin('0px', '20px'),
        '&::after': {
            backgroundColor: 'transparent',
            height: '5px',
            ...shorthands.margin('-13px'),
            ...shorthands.borderRadius('0'),
        },
        '&::before': {
            backgroundColor: 'transparent',
            height: '5px',
            ...shorthands.margin('-13px'),
            ...shorthands.borderRadius('0'),
        },
        '& > span': {
            fontFamily: customFonts.Lato,
        },
        '&[aria-selected=true]::after': {
            backgroundColor: customColors.protivitiOrange,
        },
        '&[aria-selected=true]::before': {
            backgroundColor: customColors.protivitiOrange,
        },
        '&[aria-selected=true] > span': {
            fontFamily: customFonts.Lato,
            fontWeight: '700',
            fontSize: '14px',
            lineHeight: '16px',
            color: customColors.summarizeButtonDarker,
        },
    },
    toolbar: {
        display: 'grid',
        gridTemplateColumns: 'minmax(260px, 1.5fr) repeat(3, minmax(0, 1fr))',
        ...shorthands.gap('12px'),
        alignItems: 'end',
        '@media (max-width: 1000px)': {
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        },
        '@media (max-width: 640px)': {
            gridTemplateColumns: '1fr',
        },
    },
    searchField: {
        width: '100%',
    },
    resultsSummary: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        ...shorthands.gap('12px'),
        flexWrap: 'wrap',
    },
    resultsMeta: {
        fontFamily: customFonts.Lato,
        color: customColors.buttonTextDarkBlue,
        fontSize: '14px',
        whiteSpace: 'nowrap',
    },
    secondaryButton: {
        fontFamily: customFonts.Lato,
        fontWeight: 600,
    },
    deleteButton: {
        backgroundColor: customColors.white,
        color: '#FB3B30',
        ...shorthands.border('1px', 'solid', '#FB3B30'),
        ':enabled:hover': {
            backgroundColor: '#FB3B30',
            color: customColors.white,
            ...shorthands.borderColor('#FB3B30'),
        },
        ':disabled': {
            backgroundColor: tokens.colorNeutralBackground3,
            color: tokens.colorNeutralForegroundDisabled,
            ...shorthands.borderColor(tokens.colorNeutralStroke2),
        },
    },
    listCard: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        backgroundColor: customColors.white,
        ...shorthands.borderRadius('18px'),
        paddingBottom: '10px',
        boxShadow: customColors.dropShadow1,
        overflow: 'hidden',
    },
    tableHeader: {
        display: 'grid',
        gridTemplateColumns: '44px minmax(220px, 2fr) minmax(100px, 0.8fr) minmax(140px, 1fr) minmax(170px, 1fr)',
        alignItems: 'center',
        ...shorthands.gap('12px'),
        ...shorthands.padding('14px', '18px'),
        borderBottom: `1px solid ${customColors.lightGray}`,
        fontFamily: customFonts.Barlow,
        fontWeight: 700,
        fontSize: '13px',
        color: customColors.darkNavy,
        '@media (max-width: 780px)': {
            display: 'none',
        },
    },
    scrollRegion: {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        ...AlwaysOnScrollBarStyles,
    },
    row: {
        display: 'grid',
        gridTemplateColumns: '44px minmax(220px, 2fr) minmax(100px, 0.8fr) minmax(140px, 1fr) minmax(170px, 1fr)',
        alignItems: 'center',
        ...shorthands.gap('12px'),
        ...shorthands.padding('14px', '18px'),
        borderBottom: `1px solid ${customColors.lightGray}`,
        ':last-child': {
            borderBottom: 'none',
        },
        '@media (max-width: 780px)': {
            gridTemplateColumns: '32px minmax(0, 1fr)',
            alignItems: 'start',
        },
    },
    sessionMeta: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        ...shorthands.gap('4px'),
    },
    sessionTitle: {
        fontFamily: customFonts.Lato,
        fontWeight: 400,
        color: customColors.darkNavy,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        textDecorationLine: 'underline',
    },
    titleButton: {
        minWidth: '0px',
        width: 'fit-content',
        maxWidth: '100%',
        justifyContent: 'flex-start',
        textAlign: 'left',
        color: 'inherit',
        backgroundColor: 'transparent',
        ...shorthands.border('none'),
        ...shorthands.padding('0px'),
        ':hover': {
            backgroundColor: 'transparent',
        },
    },
    currentBadge: {
        display: 'inline-flex',
        alignSelf: 'flex-start',
        fontFamily: customFonts.Barlow,
        fontSize: '11px',
        fontWeight: 700,
        color: customColors.summarizeButtonDarker,
        backgroundColor: 'rgba(1, 65, 107, 0.08)',
        ...shorthands.padding('2px', '8px'),
        ...shorthands.borderRadius('999px'),
    },
    cellText: {
        fontFamily: customFonts.Lato,
        color: customColors.buttonTextDarkBlue,
        fontSize: '14px',
        '@media (max-width: 780px)': {
            display: 'none',
        },
    },
    mobileMeta: {
        display: 'none',
        '@media (max-width: 780px)': {
            display: 'flex',
            flexDirection: 'column',
            ...shorthands.gap('2px'),
            color: customColors.buttonTextDarkBlue,
            fontFamily: customFonts.Lato,
            fontSize: '12px',
        },
    },
    emptyState: {
        minHeight: '260px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        ...shorthands.gap('8px'),
        color: customColors.buttonTextDarkBlue,
    },
    emptyTitle: {
        fontFamily: customFonts.Barlow,
        fontWeight: 700,
        color: customColors.darkNavy,
    },
    dialogActions: {
        ...shorthands.padding('18px', '28px', '24px', '28px'),
        borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
        backgroundColor: customColors.white,
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
    },
    actionButtons: {
        display: 'flex',
        alignItems: 'center',
        ...shorthands.gap('12px'),
        flexWrap: 'wrap',
    },
    hint: {
        fontFamily: customFonts.Lato,
        fontSize: '15px',
        color: customColors.buttonTextDarkBlue,
    },
    warningHint: {
        color: customColors.warningRed,
    },
});

interface ManageChatsDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    vm: ChatListVm;
}

const ManageChatsDialog: React.FC<ManageChatsDialogProps> = ({ open, onOpenChange, vm }) => {
    const styles = useStyles();
    const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);
    const {
        searchQuery,
        setSearchQuery,
        selectedTab,
        setSelectedTab,
        providerFilter,
        setProviderFilter,
        agentFilter,
        setAgentFilter,
        sortBy,
        setSortBy,
        agentOptions,
        filteredItems,
        visibleSelectableIds,
        headerCheckboxState,
        handleHeaderCheckboxChange,
    } = useManageChatsDialog({
        items: vm.manageChatItems,
        selectedIds: vm.selectedIds,
        setSelectedSessions: vm.setSelectedSessions,
        clearSelection: vm.clearSelection,
    });

    const handleDelete = async (): Promise<void> => {
        const didDelete = await vm.deleteSelectedSessions();

        if (didDelete) {
            setConfirmDeleteOpen(false);
            onOpenChange(false);
        }
    };

    const selectedChatCount = vm.selectedIds.size;
    const confirmMessage =
        selectedChatCount === 1
            ? 'Are you sure you want to delete this chat session?'
            : `Are you sure you want to delete ${selectedChatCount} chat sessions?`;

    return (
        <>
            <Dialog open={open} onOpenChange={(_event, data) => onOpenChange(data.open)}>
                <DialogSurface className={styles.surface}>
                    <DialogBody className={styles.body}>
                        <div className={styles.header}>
                            <div className={styles.headerText}>
                                <DialogTitle className={styles.title}>Manage Chats</DialogTitle>
                                <Text className={styles.subtitle}>
                                    Search by title, filter your sessions, and bulk delete chats without changing the
                                    sidebar view.
                                </Text>
                            </div>
                            <Button
                                appearance="transparent"
                                aria-label="Close manage chats dialog"
                                className={styles.closeButton}
                                icon={<Dismiss32Regular />}
                                onClick={() => onOpenChange(false)}
                            />
                        </div>

                        <DialogContent className={styles.content}>
                            <div className={styles.toolbar}>
                                <Field label="Search by title">
                                    <Input
                                        className={styles.searchField}
                                        contentBefore={<Search24Regular />}
                                        placeholder="Search chats"
                                        value={searchQuery}
                                        onChange={(event) => setSearchQuery(event.target.value)}
                                    />
                                </Field>
                                <Field label="Provider">
                                    <Select
                                        value={providerFilter}
                                        onChange={(event) =>
                                            setProviderFilter(event.target.value as ProviderFilterValue)
                                        }
                                    >
                                        <option value="all">All providers</option>
                                        <option value="progpt">ProGPT</option>
                                        <option value="nexus">Nexus</option>
                                    </Select>
                                </Field>
                                <Field label="Agent">
                                    <Select
                                        value={agentFilter}
                                        onChange={(event) => setAgentFilter(event.target.value)}
                                    >
                                        <option value="all">All agents</option>
                                        {agentOptions.map(([agentId, agentName]) => (
                                            <option key={agentId} value={agentId}>
                                                {agentName}
                                            </option>
                                        ))}
                                    </Select>
                                </Field>
                                <Field label="Sort by">
                                    <Select
                                        value={sortBy}
                                        onChange={(event) => setSortBy(event.target.value as SortValue)}
                                    >
                                        <option value="recent">Most recent</option>
                                        <option value="oldest">Oldest</option>
                                        <option value="title">Title</option>
                                    </Select>
                                </Field>
                            </div>

                            <div className={styles.listCard}>
                                <div className={styles.tabBar}>
                                    <TabList
                                        className={styles.tabListRoot}
                                        selectedValue={selectedTab}
                                        onTabSelect={(_event, data) =>
                                            setSelectedTab(data.value as ManageChatsTabValue)
                                        }
                                    >
                                        <Tab value="active" className={styles.tab}>
                                            Active Chats
                                        </Tab>
                                        <Tab value="archived" className={styles.tab}>
                                            Archived Chats
                                        </Tab>
                                    </TabList>
                                    <div className={styles.resultsSummary}>
                                        <Text className={styles.resultsMeta}>
                                            {filteredItems.length}/
                                            {vm.allowedChatCount !== null ? vm.allowedChatCount : filteredItems.length}{' '}
                                            Chats, {vm.selectedIds.size} Selected
                                        </Text>
                                    </div>
                                </div>
                                <div className={styles.tableHeader}>
                                    <Checkbox
                                        aria-label="Select all visible chats"
                                        checked={headerCheckboxState}
                                        onChange={(_event, data) => handleHeaderCheckboxChange(!!data.checked)}
                                        disabled={visibleSelectableIds.length === 0}
                                    />
                                    <span>Chat</span>
                                    <span>Provider</span>
                                    <span>Agent</span>
                                    <span>Last activity</span>
                                </div>
                                <div className={styles.scrollRegion}>
                                    {filteredItems.length > 0 ? (
                                        filteredItems.map((item) => (
                                            <div key={item.sessionId} className={styles.row}>
                                                <Checkbox
                                                    checked={vm.selectedIds.has(item.sessionId)}
                                                    onChange={() => vm.toggleSelectSession(item.sessionId)}
                                                />
                                                <div className={styles.sessionMeta}>
                                                    <Button
                                                        appearance="transparent"
                                                        className={styles.titleButton}
                                                        onClick={() => {
                                                            vm.selectSession(item.sessionId);
                                                            onOpenChange(false);
                                                        }}
                                                    >
                                                        <Text className={styles.sessionTitle}>{item.title}</Text>
                                                    </Button>
                                                    {item.isCurrent && (
                                                        <span className={styles.currentBadge}>Current chat</span>
                                                    )}
                                                    <div className={styles.mobileMeta}>
                                                        <span>{item.providerLabel}</span>
                                                        <span>{item.agentName}</span>
                                                        <span>{item.modifiedText}</span>
                                                    </div>
                                                </div>
                                                <Text className={styles.cellText}>{item.providerLabel}</Text>
                                                <Text className={styles.cellText}>{item.agentName}</Text>
                                                <Text className={styles.cellText}>{item.modifiedText}</Text>
                                            </div>
                                        ))
                                    ) : (
                                        <div className={styles.emptyState}>
                                            <Text className={styles.emptyTitle}>
                                                No chats match the current filters
                                            </Text>
                                            <Text>Try clearing a filter or searching with a different title.</Text>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </DialogContent>

                        <DialogActions className={styles.dialogActions}>
                            <Text
                                className={mergeClasses(
                                    styles.hint,
                                    vm.selectedIds.size > 0 ? styles.warningHint : undefined,
                                )}
                            >
                                {vm.selectedIds.size > 0
                                    ? 'Warning: Deleted chat sessions cannot be recovered.'
                                    : 'Select chats you wish to delete'}
                            </Text>
                            <div className={styles.actionButtons}>
                                <Button
                                    className={mergeClasses(styles.secondaryButton, styles.deleteButton)}
                                    onClick={() => setConfirmDeleteOpen(true)}
                                    disabled={vm.selectedIds.size === 0 || vm.isDeletingMultiple}
                                >
                                    Delete
                                </Button>
                            </div>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
            <ConfirmDialog
                open={confirmDeleteOpen}
                message={confirmMessage}
                onConfirm={() => {
                    void handleDelete();
                }}
                onCancel={() => {
                    if (!vm.isDeletingMultiple) {
                        setConfirmDeleteOpen(false);
                    }
                }}
                isLoading={vm.isDeletingMultiple}
            />
        </>
    );
};

export default ManageChatsDialog;
